import React, { createContext, useContext, useState, useEffect } from 'react';

export interface TourState {
  isActive: boolean;
  currentStep: number;
  isCompleted: boolean;
}

interface TourContextType {
  state: TourState;
  setState: (state: TourState) => void;
  startTour: () => void;
  forceStartTour: () => void;
  completeStep: (step: number) => void;
  closeTour: () => void;
  skipTour: () => void;
  resetTour: () => void;
  completeTour: () => Promise<boolean>;
}

const defaultTourState: TourState = {
  isActive: false,
  currentStep: 1,
  isCompleted: false,
};

const TourContext = createContext<TourContextType | undefined>(undefined);

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

interface TourProviderProps {
  children: React.ReactNode;
}

export const TourProvider: React.FC<TourProviderProps> = ({ children }) => {
  // Tour-State aus localStorage laden oder Default verwenden
  const [state, setState] = useState<TourState>(() => {
    try {
      const saved = localStorage.getItem('tourState');
      if (saved) {
        const parsed = JSON.parse(saved);
        return { ...defaultTourState, ...parsed };
      }
    } catch (error) {
      console.error('Fehler beim Laden des Tour-States:', error);
    }
    return defaultTourState;
  });

  // Tour starten
  const startTour = () => {
    console.log('🚀 TourContext: Starte Tour...');
    console.log('🚀 TourContext: Aktueller State vor Start:', state);
    const newState = {
      isActive: true,
      currentStep: 1,
      isCompleted: false,
    };
    setState(newState);
    localStorage.setItem('tourState', JSON.stringify(newState));
    console.log('🚀 TourContext: Neuer State gesetzt:', newState);
  };

  // Tour manuell starten (für Testing)
  const forceStartTour = () => {
    console.log('🔧 TourContext: Force Start Tour (für Testing)...');
    const newState = {
      isActive: true,
      currentStep: 1,
      isCompleted: false,
    };
    setState(newState);
    localStorage.setItem('tourState', JSON.stringify(newState));
    localStorage.removeItem('tourStarted'); // Tour-Flag zurücksetzen
    console.log('✅ Tour manuell gestartet für Testing');
  };

  // Schritt abschließen
  const completeStep = (step: number) => {
    console.log('✅ Tour-Schritt abgeschlossen:', step);
    console.log('✅ Aktueller State vor Änderung:', state);
    const newState = {
      ...state,
      currentStep: step + 1,
    };
    console.log('✅ Neuer State nach Änderung:', newState);
    setState(newState);
    localStorage.setItem('tourState', JSON.stringify(newState));
    console.log('✅ State gesetzt und in localStorage gespeichert');
  };

  // Tour einfach schließen (ohne API-Call)
  const closeTour = () => {
    console.log('❌ Tour geschlossen');
    
    const newState = {
      isActive: false,
      currentStep: 1,
      isCompleted: false, // Nicht als abgeschlossen markieren
    };
    setState(newState);
    localStorage.setItem('tourState', JSON.stringify(newState));
    
    // Tour-Flags zurücksetzen
    localStorage.removeItem('tourStarted');
    console.log('✅ Tour geschlossen und Flags zurückgesetzt');
  };

  // Tour überspringen (mit API-Call)
  const skipTour = async () => {
    console.log('⏭️ Tour übersprungen');
    
    try {
      // Markiere Tour als abgeschlossen in der API (auch wenn übersprungen)
      const { getTourManager } = await import('../utils/tourManager');
      const tourManager = getTourManager();
      const success = await tourManager.markTourCompleted();
      
      if (success) {
        console.log('✅ Tour erfolgreich als übersprungen markiert');
      } else {
        console.log('⚠️ Tour konnte nicht als übersprungen markiert werden');
      }
    } catch (error) {
      console.error('❌ Fehler beim Markieren der Tour als übersprungen:', error);
    }
    
    const newState = {
      isActive: false,
      currentStep: 1,
      isCompleted: true, // Als abgeschlossen markieren, damit sie nicht wieder startet
    };
    setState(newState);
    localStorage.setItem('tourState', JSON.stringify(newState));
    
    // Tour-Flags zurücksetzen, damit Tour nicht wieder startet
    localStorage.removeItem('tourStarted');
    console.log('✅ Tour-Flags zurückgesetzt');
  };

  // Tour zurücksetzen
  const resetTour = () => {
    console.log('🔄 Tour zurückgesetzt');
    const newState = {
      isActive: false,
      currentStep: 1,
      isCompleted: false,
    };
    setState(newState);
    localStorage.setItem('tourState', JSON.stringify(newState));
    
    // Tour-Flags zurücksetzen, damit Tour nicht wieder startet
    localStorage.removeItem('tourStarted');
    console.log('✅ Tour-Flags zurückgesetzt');
  };

  // Tour abschließen
  const completeTour = async () => {
    try {
      console.log('🎉 Tour wird abgeschlossen...');
      
      // Markiere Tour als abgeschlossen in der API
      const { getTourManager } = await import('../utils/tourManager');
      const tourManager = getTourManager();
      const success = await tourManager.markTourCompleted();
      
      if (success) {
        console.log('✅ Tour erfolgreich abgeschlossen');
        const newState = {
          isActive: false,
          currentStep: 1,
          isCompleted: true,
        };
        setState(newState);
        localStorage.setItem('tourState', JSON.stringify(newState));
        
        // Tour-Flags zurücksetzen, damit Tour nicht wieder startet
        localStorage.removeItem('tourStarted');
        console.log('✅ Tour-Flags zurückgesetzt');
        return true;
      } else {
        console.error('❌ Fehler beim Abschließen der Tour');
        return false;
      }
    } catch (error) {
      console.error('❌ Fehler beim Tour-Abschluss:', error);
      return false;
    }
  };

  return (
    <TourContext.Provider value={{
      state,
      setState,
      startTour,
      forceStartTour,
      completeStep,
      closeTour,
      skipTour,
      resetTour,
      completeTour,
    }}>
      {children}
    </TourContext.Provider>
  );
};
